import { hasDeployDatabaseImage } from "@app/deploy";
import type { DeployDatabase, DeployDatabaseImage } from "@app/types";
import type { PopoverProps } from "../popover";
import { Popover } from "../popover";

const PITR_DOCS_URL =
  "https://www.aptible.com/docs/core-concepts/managed-databases/managing-databases/database-backups#point-in-time-recovery-for-postgresql";

export const PitrStatusPopover = ({
  database,
  image,
  trigger,
  placement = "top",
}: {
  database: DeployDatabase;
  image: DeployDatabaseImage;
  trigger: React.ReactNode;
  placement?: PopoverProps["placement"];
}) => {
  let content: React.ReactNode;

  if (!database.enableBackups) {
    content = (
      <span>Point-in-time recovery (PITR) requires backups to be enabled.</span>
    );
  } else if (
    database.type === "postgresql" &&
    database.pitrStatus === "unsupported"
  ) {
    content = (
      <span>
        Point-in-time recovery is unsupported for this database version. Only
        PostgreSQL version 13 or newer is supported. See{" "}
        <a
          href={PITR_DOCS_URL}
          target="_blank"
          rel="noreferrer"
          className="underline"
        >
          PITR docs
        </a>{" "}
        for more information.
      </span>
    );
  } else if (database.pitrStatus === "restricted") {
    content = (
      <span>
        Point-in-time recovery (PITR) is unsupported for this database due to
        its extremely high write volume. See{" "}
        <a
          href={PITR_DOCS_URL}
          target="_blank"
          rel="noreferrer"
          className="underline"
        >
          PITR docs
        </a>{" "}
        for more information on limitations.
      </span>
    );
  } else if (database.pitrStatus === "pending_restart") {
    content = (
      <span>
        Please restart this database. Once restarted, PITR will be automatically
        enabled.
      </span>
    );
  } else if (hasDeployDatabaseImage(image) && !image.pitrSupported) {
    content = (
      <span>
        Point-in-time recovery is unsupported for this database type. See{" "}
        <a
          href={PITR_DOCS_URL}
          target="_blank"
          rel="noreferrer"
          className="underline"
        >
          PITR docs
        </a>{" "}
        for more information.
      </span>
    );
  } else {
    // Pre-GA: PITR is supported but hasn't been automatically enabled yet
    // Post-GA: no one will be in this state
    content = <span>Point-in-time recovery is disabled.</span>;
  }

  return (
    <Popover placement={placement} trigger={trigger}>
      {content}
    </Popover>
  );
};
